import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import { vacancyStore } from 'stores/vacancyStore';
import { personalDataStore } from 'stores/personalDataStore';
import type { NewVacancy } from 'stores/vacancyStore';
import type { VacancySkills } from 'api';
import { PATHS } from 'routes/paths';

const validationSchema = Yup.object<Record<keyof NewVacancy, Yup.AnySchema>>({
  speciality: Yup.string()
    .trim()
    .required('Укажите сферу деятельности')
    .max(100, 'Слишком длинное значение'),
  specialityLevel: Yup.mixed<NewVacancy['specialityLevel']>()
    .oneOf(['JUNIOR', 'MIDDLE', 'SENIOR'], 'Выберите уровень')
    .required('Выберите уровень'),
  position: Yup.string().trim().required('Укажите должность').max(100, 'Слишком длинное значение')
});

type FieldErrors = Partial<Record<keyof NewVacancy, string>>;

/** Редактируемая строка навыка. cutoff — минимальный порог оценки (1–10) либо null. */
type SkillRow = {
  id: string;
  name: string;
  cutoff: number | null;
};

const CUTOFF_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

// Непустое значение для пункта «Не задан»: MUI Select не отображает выбранный
// пункт с пустым value (''), поэтому для null используем отдельный сентинел.
const CUTOFF_NONE = 'none';

// Вакансию можно создать только с ровно таким числом навыков.
const MAX_SKILLS = 7;

/**
 * Собрать тело PATCH /vacancy/:id/skills из строк редактора.
 * Пропускаем строки с пустым названием; при дублях побеждает последняя.
 */
const buildSkillsPayload = (rows: SkillRow[]): VacancySkills => {
  const payload: VacancySkills = {};
  rows.forEach(({ name, cutoff }) => {
    const key = name.trim();
    if (!key) return;
    payload[key] = cutoff;
  });
  return payload;
};

const NewVacancyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = personalDataStore;
  const companyId =
    (location.state as { companyId?: string } | null)?.companyId ?? user?.company?.id;
  const { newVacancy, setNewVacancyField, createVacancy } = vacancyStore;
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [specializationInput, setSpecializationInput] = useState(newVacancy.speciality ?? '');
  const [positionInput, setPositionInput] = useState(newVacancy.position ?? '');
  const [description, setDescription] = useState('');

  // Навыки определяются вручную (кнопкой) ещё до создания вакансии — id для этого
  // не нужен. Сохраняются они уже после создания, в handleSubmit, по полученному id.
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [skillsRevealed, setSkillsRevealed] = useState(false);
  const [skillRows, setSkillRows] = useState<SkillRow[]>([]);

  // id уже созданной вакансии: если сохранение навыков упало, повторный сабмит
  // не создаёт дубль вакансии, а лишь повторяет PATCH навыков.
  const [createdVacancyId, setCreatedVacancyId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Считаем по итоговому payload — это число уникальных непустых навыков,
  // которое реально уйдёт в PATCH (дубли и пустые строки не учитываются).
  const skillsCount = useMemo(() => Object.keys(buildSkillsPayload(skillRows)).length, [skillRows]);
  const hasRequiredSkills = skillsCount === MAX_SKILLS;

  const selectedSpecializationId = useMemo(() => {
    const found = personalDataStore.specializations.find((s) => s.name === newVacancy.speciality);
    return found?.id ? Number(found.id) : null;
  }, [newVacancy.speciality, personalDataStore.specializations]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (newVacancy.position && !specializationInput) {
        personalDataStore.fetchSpecializationsByPositionName(newVacancy.position);
      } else {
        personalDataStore.fetchSpecializations(specializationInput);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [specializationInput, newVacancy.position]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedSpecializationId) {
        personalDataStore.fetchPositionsBySpecializationId(positionInput, selectedSpecializationId);
      } else {
        personalDataStore.fetchAllPositions(positionInput);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [positionInput, selectedSpecializationId]);

  const validateField = async (field: keyof NewVacancy) => {
    try {
      await validationSchema.validateAt(field, newVacancy);
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setFieldErrors((prev) => ({ ...prev, [field]: err.message }));
      }
    }
  };

  const validateForm = async (): Promise<boolean> => {
    try {
      await validationSchema.validate(newVacancy, { abortEarly: false });
      setFieldErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const nextErrors: FieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path && e.message) {
            const key = e.path as keyof NewVacancy;
            if (!nextErrors[key]) nextErrors[key] = e.message;
          }
        });
        setFieldErrors(nextErrors);
      }
      return false;
    }
  };

  const handleChange = (field: keyof NewVacancy) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewVacancyField(field, value as NewVacancy[typeof field]);
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSpecialityChange = (_event: unknown, newValue: string | null) => {
    setNewVacancyField('speciality', newValue ?? '');
    if (fieldErrors.speciality) {
      setFieldErrors((prev) => ({ ...prev, speciality: undefined }));
    }
  };

  const handlePositionChange = (_event: unknown, newValue: string | null) => {
    setNewVacancyField('position', newValue ?? '');
    if (fieldErrors.position) {
      setFieldErrors((prev) => ({ ...prev, position: undefined }));
    }
  };

  /**
   * Ручной запуск подбора навыков (POST /vacancy/skills). id вакансии не нужен —
   * достаточно описательных полей. Результат раскрывает редактируемый список.
   */
  const handleGenerate = async () => {
    if (!(await validateForm())) return;
    setGenError(null);
    setGenerating(true);
    try {
      const skills = await vacancyStore.generateSkills({
        position: newVacancy.position,
        speciality: newVacancy.speciality,
        level: newVacancy.specialityLevel,
        description: description.trim()
      });
      setSkillRows(skills.map((name) => ({ id: uuidv4(), name, cutoff: null })));
      setSkillsRevealed(true);
    } catch {
      setGenError('Не удалось подобрать навыки. Попробуйте ещё раз или добавьте навыки вручную.');
    } finally {
      setGenerating(false);
    }
  };

  const updateRow = (id: string, patch: Partial<Omit<SkillRow, 'id'>>) => {
    setSkillRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string) => {
    setSkillRows((rows) => rows.filter((row) => row.id !== id));
  };

  const addRow = () => {
    setSkillsRevealed(true);
    setSkillRows((rows) => [...rows, { id: uuidv4(), name: '', cutoff: null }]);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!companyId) {
      setFieldErrors({ position: 'Сначала создайте или выберите компанию' });
      return;
    }
    if (!hasRequiredSkills) {
      setSubmitError(`Добавьте ровно ${MAX_SKILLS} навыков (сейчас ${skillsCount}).`);
      return;
    }
    if (!(await validateForm())) return;

    setSubmitting(true);
    try {
      // Вакансию создаём один раз: при повторном сабмите (после сбоя сохранения
      // навыков) переиспользуем уже полученный id.
      let vacancyId = createdVacancyId;
      if (!vacancyId) {
        const vacancy = await createVacancy({
          companyId,
          speciality: newVacancy.speciality,
          specialityLevel: newVacancy.specialityLevel,
          position: newVacancy.position
        });
        vacancyId = vacancy.id;
        setCreatedVacancyId(vacancyId);
      }

      const payload = buildSkillsPayload(skillRows);
      if (Object.keys(payload).length > 0) {
        await vacancyStore.saveSkills(vacancyId, payload);
      }

      navigate(companyId ? PATHS.hr.company : PATHS.main, { replace: true });
    } catch {
      setSubmitError('Не удалось сохранить вакансию. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        mr: -3,
        pr: 3,
        ml: -3,
        pl: 3,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack spacing={3} sx={{ width: '100%', maxWidth: 712, mx: 'auto', my: 'auto', py: 4 }}>
        {/* Блок 1 — основные поля вакансии */}
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={3}>
              <Typography color="text.secondary" variant="body2">
                Укажите сферу деятельности, уровень и должность для новой вакансии
              </Typography>

              <Box sx={{ position: 'relative', pb: 3 }}>
                <Autocomplete
                  fullWidth
                  options={personalDataStore.specializations.map((s) => s.name)}
                  value={newVacancy.speciality || null}
                  onChange={handleSpecialityChange}
                  onBlur={() => validateField('speciality')}
                  inputValue={specializationInput}
                  onInputChange={(_event, newInputValue) => setSpecializationInput(newInputValue)}
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Сфера деятельности"
                      size="medium"
                      required
                      error={Boolean(fieldErrors.speciality)}
                      helperText={fieldErrors.speciality}
                      sx={{ position: 'relative' }}
                      FormHelperTextProps={{
                        sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                      }}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Сфера деятельности'
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option}
                    </Box>
                  )}
                  noOptionsText="Сфера деятельности не найдена"
                  clearOnEscape
                />
              </Box>

              <Box sx={{ position: 'relative', pb: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Уровень"
                  value={newVacancy.specialityLevel}
                  onChange={handleChange('specialityLevel')}
                  onBlur={() => validateField('specialityLevel')}
                  size="medium"
                  required
                  error={Boolean(fieldErrors.specialityLevel)}
                  helperText={fieldErrors.specialityLevel}
                  sx={{ position: 'relative' }}
                  FormHelperTextProps={{
                    sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                  }}
                  inputProps={{ 'aria-label': 'Уровень' }}
                >
                  <MenuItem value="JUNIOR">Начинающий</MenuItem>
                  <MenuItem value="MIDDLE">С опытом</MenuItem>
                  <MenuItem value="SENIOR">Эксперт</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ position: 'relative', pb: 3 }}>
                <Autocomplete
                  fullWidth
                  options={personalDataStore.positions.map((p) => p.name)}
                  value={newVacancy.position || null}
                  onChange={handlePositionChange}
                  onBlur={() => validateField('position')}
                  inputValue={positionInput}
                  onInputChange={(_event, newInputValue) => setPositionInput(newInputValue)}
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Должность"
                      size="medium"
                      required
                      error={Boolean(fieldErrors.position)}
                      helperText={fieldErrors.position}
                      sx={{ position: 'relative' }}
                      FormHelperTextProps={{
                        sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                      }}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Должность'
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option}
                    </Box>
                  )}
                  noOptionsText="Должность не найдена"
                  clearOnEscape
                />
              </Box>

              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Описание вакансии"
                  placeholder="Кратко опишите задачи и требования — по описанию подберём навыки"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  size="medium"
                  inputProps={{ 'aria-label': 'Описание вакансии' }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Блок 2 — навыки: ручной подбор и редактирование */}
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Навыки
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Определите навыки по описанию и при необходимости отредактируйте список.
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: hasRequiredSkills
                      ? 'success.main'
                      : skillsCount > MAX_SKILLS
                      ? 'error.main'
                      : 'text.secondary'
                  }}
                >
                  {skillsCount} из {MAX_SKILLS} навыков
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                onClick={handleGenerate}
                disabled={generating || submitting}
                startIcon={
                  generating ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Icon icon="mdi:auto-fix" width={20} height={20} />
                  )
                }
                sx={{ alignSelf: 'flex-start' }}
              >
                {generating
                  ? 'Определяем навыки…'
                  : skillsRevealed
                  ? 'Определить навыки заново'
                  : 'Определить навыки'}
              </Button>

              {genError && <Alert severity="warning">{genError}</Alert>}

              {skillsRevealed && (
                <Stack spacing={2}>
                  <Typography color="text.secondary" variant="body2">
                    Задайте каждому навыку минимальный порог оценки (1–10) либо оставьте «Не задан».<br />
                    Исходя из этого набора навыков система будет формировать вопросы для соискателя.
                  </Typography>

                  {skillRows.length === 0 && (
                    <Typography color="text.secondary" variant="body2" textAlign="center">
                      Навыков пока нет — добавьте их вручную.
                    </Typography>
                  )}

                  {skillRows.map((row) => (
                    <Stack key={row.id} direction="row" spacing={1.5} alignItems="flex-start">
                      <TextField
                        fullWidth
                        size="small"
                        label="Навык"
                        value={row.name}
                        onChange={(event) => updateRow(row.id, { name: event.target.value })}
                        inputProps={{ 'aria-label': 'Название навыка' }}
                      />
                      <TextField
                        select
                        size="small"
                        label="Порог"
                        value={row.cutoff === null ? CUTOFF_NONE : String(row.cutoff)}
                        onChange={(event) =>
                          updateRow(row.id, {
                            cutoff:
                              event.target.value === CUTOFF_NONE
                                ? null
                                : Number(event.target.value)
                          })
                        }
                        sx={{ minWidth: 130 }}
                        inputProps={{ 'aria-label': 'Минимальный порог оценки' }}
                      >
                        <MenuItem value={CUTOFF_NONE}>Не задан</MenuItem>
                        {CUTOFF_OPTIONS.map((value) => (
                          <MenuItem key={value} value={String(value)}>
                            {value}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Tooltip title="Удалить навык">
                        <IconButton
                          aria-label="Удалить навык"
                          onClick={() => removeRow(row.id)}
                          sx={{ mt: 0.5 }}
                        >
                          <Icon icon="mdi:close" width={20} height={20} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}

                  <Button
                    variant="text"
                    onClick={addRow}
                    disabled={skillsCount >= MAX_SKILLS}
                    startIcon={<Icon icon="mdi:plus" width={20} height={20} />}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Добавить навык
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Сабмит — создание вакансии и сохранение навыков */}
        <Stack spacing={2} alignItems="center" sx={{ pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {submitError}
            </Alert>
          )}
          <Button
            size="large"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || generating || !hasRequiredSkills}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            aria-label="Создать вакансию"
            sx={{ width: '250px' }}
          >
            {submitting ? 'Создаём…' : 'Создать вакансию'}
          </Button>
          {!hasRequiredSkills && (
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Чтобы создать вакансию, нужно ровно {MAX_SKILLS} навыков (сейчас {skillsCount}).
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default observer(NewVacancyPage);
